import {
  GaiaError,
  HTTPClientError,
  ResponseValidationError,
  SDKValidationError,
} from '@repo/sdk/models/errors';

/**
 * Type guard to check if an error is a GaiaError (which SDKError extends)
 */
export function isGaiaError(error: unknown): error is GaiaError {
  return error instanceof Error && 'body' in error && 'statusCode' in error;
}

/**
 * Type guard to check if an error is an SDKValidationError
 */
export function isSDKValidationError(error: unknown): error is SDKValidationError {
  return error instanceof SDKValidationError;
}

/**
 * Type guard to check if an error is a ResponseValidationError
 */
export function isResponseValidationError(error: unknown): error is ResponseValidationError {
  return error instanceof ResponseValidationError;
}

/**
 * Interface for the error response body from the API
 */
interface ApiErrorBody {
  name?: string;
  id?: string;
  message?: string;
  temporary?: boolean;
  timeout?: boolean;
  fault?: boolean;
}

/**
 * Extract the user-friendly error message from an SDK error
 *
 * @param error - The error object from the SDK
 * @returns The extracted message or the full error message as fallback
 *
 * @example
 * try {
 *   await updateSettings(settings);
 * } catch (error) {
 *   const message = getApiErrorMessage(error);
 *   toast.error(message); // "access restricted in read only mode"
 * }
 */
export function getApiErrorMessage(error: unknown): string {
  // Handle SDKValidationError - use the pretty() method for formatted output
  if (isSDKValidationError(error)) {
    return error.pretty();
  }

  // Handle ResponseValidationError - use the pretty() method for formatted output
  if (isResponseValidationError(error)) {
    return error.pretty();
  }

  // Handle SDKError specifically - extract from message
  if (error instanceof Error && error.name === 'SDKError') {
    // SDKError message format: "API error occurred: Status 403 Body: {...}"
    const messageMatch = error.message.match(/Body: ({.*})$/);
    if (messageMatch) {
      try {
        const errorBody: ApiErrorBody = JSON.parse(messageMatch[1]);
        if (errorBody.message) {
          return errorBody.message;
        }
        if (errorBody.name) {
          return errorBody.name;
        }
      } catch {
        // If JSON parsing fails, fall through to default
      }
    }
  }

  // Handle GaiaError (includes other SDKError types) - extract from body
  if (isGaiaError(error)) {
    try {
      const errorBody: ApiErrorBody = JSON.parse(error.body);

      // Return the message field if it exists
      if (errorBody.message) {
        return errorBody.message;
      }

      // Fallback to name if no message
      if (errorBody.name) {
        return errorBody.name;
      }
    } catch {
      // If JSON parsing fails, fall through to default
    }
  }

  // Handle HTTPClientError
  if (error instanceof HTTPClientError) {
    return error.message;
  }

  // Handle any other Error
  if (error instanceof Error) {
    return error.message;
  }

  // Final fallback
  return String(error);
}
